import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

// Lightweight toast queue for transient messages (offline save, back
// online, generic errors). Designed for low frequency — at most a
// couple of toasts at a time. The provider mounts a single fixed
// portal at the bottom of the screen; the consumer just calls
// showToast({ message }) from anywhere.
//
// Why a context (not a global event bus): toasts need to render through
// the React tree so they participate in theme + maxWidth shell. The
// queue lives in state so re-mounts (route changes) don't drop pending
// toasts.

const ToastContext = createContext(null);

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  // Track the last shown message so the same one isn't enqueued back-
  // to-back (e.g. multiple safeWrite failures in quick succession all
  // tripping the offline toast — we want one, not five).
  const lastMessageRef = useRef(null);
  const lastShownAtRef = useRef(0);

  // Two-phase dismiss: first mark the toast as `leaving` so the CSS
  // exit animation can play, then remove it from the queue 220ms
  // later. Lets users see the bubble slide away instead of pop out.
  const dismiss = useCallback((id) => {
    setToasts((q) => q.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => {
      setToasts((q) => q.filter((t) => t.id !== id));
    }, 220);
  }, []);

  const showToast = useCallback((opts) => {
    const message = typeof opts === 'string' ? opts : opts?.message;
    if (!message) return;
    const tone = (typeof opts === 'object' && opts?.tone) || 'info';
    const duration = (typeof opts === 'object' && opts?.duration) || 3500;
    const now = Date.now();
    if (lastMessageRef.current === message && now - lastShownAtRef.current < 1500) {
      // Same message just shown; skip the duplicate.
      return;
    }
    lastMessageRef.current = message;
    lastShownAtRef.current = now;
    const id = nextId++;
    setToasts((q) => [...q.slice(-2), { id, message, tone, leaving: false }]);
    setTimeout(() => dismiss(id), duration);
  }, [dismiss]);

  const value = { showToast, dismiss };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Safe fallback when a component is rendered outside the provider
    // (shouldn't happen in practice, but tests / Storybook may).
    return { showToast: () => {}, dismiss: () => {} };
  }
  return ctx;
}

// ── Viewport ─────────────────────────────────────────────────────────

function ToastViewport({ toasts, onDismiss }) {
  if (toasts.length === 0) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 'calc(16px + env(safe-area-inset-bottom, 0px) + 72px)',
        zIndex: 200,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        padding: '0 16px',
        pointerEvents: 'none',
      }}
    >
      {toasts.map((t) => (
        <ToastBubble key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastBubble({ toast, onDismiss }) {
  const { id, message, tone, leaving } = toast;
  const colors = {
    info: { bg: 'var(--surface)', border: 'var(--border)', text: 'var(--dark-brown)' },
    success: { bg: 'var(--surface)', border: 'var(--primary)', text: 'var(--primary)' },
    warn: { bg: 'var(--nude)', border: 'var(--beige-dark)', text: 'var(--dark-brown)' },
  }[tone] || { bg: 'var(--surface)', border: 'var(--border)', text: 'var(--dark-brown)' };

  return (
    <button
      type="button"
      onClick={() => onDismiss(id)}
      className={leaving ? 'bb-toast-leave' : 'bb-toast-enter'}
      style={{
        pointerEvents: 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        maxWidth: 460,
        width: '100%',
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        color: colors.text,
        borderRadius: 12,
        padding: '12px 16px',
        fontFamily: 'var(--font-ui)',
        fontSize: 14,
        fontWeight: 500,
        textAlign: 'left',
        boxShadow: '0 6px 20px rgba(45, 26, 31, 0.12)',
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
      }}
      aria-label={message}
    >
      {tone !== 'info' && <ToastIcon tone={tone} />}
      <span style={{ flex: 1 }}>{message}</span>
    </button>
  );
}

// Leading icons for non-info toasts so the tone reads at a glance for
// users who can't or don't read the colour cue. Inherits color from
// the parent (the toast's `colors.text`).
function ToastIcon({ tone }) {
  const props = {
    width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none',
    'aria-hidden': true,
    style: { flexShrink: 0 },
  };
  const stroke = { stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (tone === 'success') {
    return (
      <svg {...props}>
        <circle cx="12" cy="12" r="9" {...stroke} />
        <path d="M8 12.5l2.5 2.5L16 9.5" {...stroke} />
      </svg>
    );
  }
  if (tone === 'warn') {
    return (
      <svg {...props}>
        <path d="M12 4l9 16H3L12 4z" {...stroke} />
        <path d="M12 11v4" {...stroke} />
        <circle cx="12" cy="17.5" r="0.6" fill="currentColor" />
      </svg>
    );
  }
  return null;
}

// One-time CSS injection for the slide-up animation. Lives here (not
// in components.css) so the toast module is self-contained — drop the
// provider in App.jsx and it works without a stylesheet edit.
if (typeof document !== 'undefined' && !document.getElementById('bb-toast-anim')) {
  const style = document.createElement('style');
  style.id = 'bb-toast-anim';
  style.textContent = `
@keyframes bbToastEnter {
  from { transform: translateY(12px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}
@keyframes bbToastLeave {
  from { transform: translateY(0);    opacity: 1; }
  to   { transform: translateY(8px);  opacity: 0; }
}
.bb-toast-enter { animation: bbToastEnter 220ms cubic-bezier(0.32, 0.72, 0, 1); }
.bb-toast-leave { animation: bbToastLeave 220ms cubic-bezier(0.32, 0.72, 0, 1) forwards; pointer-events: none; }
@media (prefers-reduced-motion: reduce) {
  .bb-toast-enter, .bb-toast-leave { animation: none !important; }
}
`;
  document.head.appendChild(style);
}
